import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { categories } from '../../data/categories';
import { easeOut } from '../../utils/motion';
import { FormField } from '../checkout/FormField';
import { btnPrimary } from '../ui/buttonStyles';
interface ApplicationData {
  name: string;
  email: string;
  discipline: string;
  link: string;
  message: string;
}
type Errors = Partial<Record<keyof ApplicationData, string>>;
const empty: ApplicationData = {
  name: '',
  email: '',
  discipline: '',
  link: '',
  message: ''
};
export function ApplicationForm() {
  const [data, setData] = useState<ApplicationData>(empty);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const set = (key: keyof ApplicationData, value: string) => {
    setData(d => ({
      ...d,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!data.name.trim()) next.name = 'Votre nom ou celui de votre atelier.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) next.email = 'Adresse e-mail invalide.';
    if (!data.discipline) next.discipline = 'Choisissez un univers.';
    if (data.message.trim().length < 20) next.message = 'Quelques lignes sur votre travail (20 caractères min.).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 900);
  };
  return <AnimatePresence mode="wait" initial={false} data-id="element-206" data-mp="1u45d0ug8kc0y">
      {status === 'sent' ? <motion.div key="sent" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: easeOut
    }} className="rounded-md bg-card p-8" role="status" data-id="element-207" data-mp="1p5lrgv5k1294">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sage text-paper" data-id="element-208" data-mp="1z26xnjtb8jej">
            <CheckIcon className="h-5 w-5" data-id="element-209" data-mp="265n5nv1lr4s2" />
          </span>
          <p className="mt-4 font-display text-3xl" data-id="element-210" data-mp="2b7cj1hcarujb">Merci {data.name.split(' ')[0]} !</p>
          <p className="mt-2 text-muted" data-id="element-211" data-mp="2g4mdxciggyej">
            Votre candidature est bien arrivée. Le collectif se réunit chaque mois pour composer les saisons à venir — nous
            vous répondrons d’ici quatre semaines.
          </p>
        </motion.div> : <motion.form key="form" onSubmit={submit} noValidate exit={{
      opacity: 0
    }} className="grid gap-4 sm:grid-cols-2" data-id="element-212" data-mp="2twsjy5rw9p09">
          <FormField id="app-name" label="Nom / atelier" value={data.name} onChange={e => set('name', e.target.value)} error={errors.name} data-id="element-213" data-mp="2yvvrbwvck7be" />
          <FormField id="app-email" label="E-mail" type="email" value={data.email} onChange={e => set('email', e.target.value)} error={errors.email} data-id="element-214" data-mp="33ugotoj7scw5" />
          <div data-id="element-215" data-mp="19gfikilc4nm1">
            <label htmlFor="app-discipline" className="mb-1.5 block text-sm" data-id="element-216" data-mp="19i0cq73qm4jd">
              Univers
            </label>
            <select id="app-discipline" value={data.discipline} onChange={e => set('discipline', e.target.value)} aria-invalid={errors.discipline ? true : undefined} className={`h-12 w-full rounded-md border bg-card px-3 text-sm outline-none focus:border-ink ${errors.discipline ? 'border-clay' : 'border-line'}`} data-id="element-217" data-mp="11z0tc1ga0d00">
              <option value="" data-id="element-218" data-mp="1kvskex227wem">Choisir…</option>
              {categories.map(c => <option key={c.id} value={c.id} data-id="element-219" data-mp="1fy0h76vysnv4">
                  {c.name}
                </option>)}
            </select>
            {errors.discipline && <p className="mt-1.5 text-xs text-clay" data-id="element-220" data-mp="15zt5p3kg2y0i">{errors.discipline}</p>}
          </div>
          <FormField id="app-link" label="Site ou Instagram" optional value={data.link} onChange={e => set('link', e.target.value)} placeholder="@votre.atelier" data-id="element-221" data-mp="3vodd3a8x3mqi" />
          <div className="sm:col-span-2" data-id="element-222" data-mp="3qrmv2hdjyh2g">
            <label htmlFor="app-message" className="mb-1.5 block text-sm" data-id="element-223" data-mp="1jds9if4fr1wm">
              Parlez-nous de votre travail
            </label>
            <textarea id="app-message" rows={4} value={data.message} onChange={e => set('message', e.target.value)} aria-invalid={errors.message ? true : undefined} className={`w-full resize-none rounded-md border bg-card px-4 py-3 text-sm outline-none focus:border-ink ${errors.message ? 'border-clay' : 'border-line'}`} data-id="element-224" data-mp="1o6ld2zo64474" />
            {errors.message && <p className="mt-1.5 text-xs text-clay" data-id="element-225" data-mp="1s6ni9it43fr5">{errors.message}</p>}
          </div>
          <div className="sm:col-span-2" data-id="element-226" data-mp="1s8zvjinxd1xv">
            <button type="submit" disabled={status === 'sending'} className={btnPrimary} data-id="element-227" data-mp="1x4p0y2mmld23">
              {status === 'sending' ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-228" data-mp="gttdsdfb9rh4" /> Envoi…
                </> : 'Envoyer ma candidature'}
            </button>
          </div>
        </motion.form>}
    </AnimatePresence>;
}